import {
  Arrange,
  Button,
  Checkbox,
  FormField,
  Link,
  Spacer,
  SvgArrowBack,
  SvgArrowForward,
  Text,
  TextButton,
  TextInput,
} from '@loomhq/lens';
import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch } from 'react-redux';

import {
  updateLoginEmailError,
  updateLoginPasswordError,
  updateLoginPlainError,
} from 'actions/login';
import { INVISIBLE_SITE_KEY } from 'constants/captcha';
import {
  EMAIL_SIGNUP_BUTTON_CLICKED,
  EMAIL_SIGNUP_FAILED,
  SIGNUP_CLICKED_PRIVACY,
  SIGNUP_CLICKED_TERMS,
  SIGNUP_EMAIL_FIELD_CLICKED,
  SIGNUP_TOGGLE_TERMS_CHECKBOX,
} from 'constants/events';
import { SIGN_UP_PAGE } from 'constants/onboarding';
import { PRIVACY_POLICY, TERMS } from 'constants/routes';
import { signUpUserCreator } from 'creators/login';
import * as analytics from 'utilities/analytics';
import { isMobile } from 'utilities/device';
import { incrementMetric } from 'utilities/metrics';

import {
  email as validateEmail,
  explainPasswordViolations,
  invalidUserFirstName,
  validatePassword,
} from '../../../../../shared/validate';

import { MaskedInput } from 'components/signup/email-signup-form/masked-input';
import PasswordFeedback from 'components/signup/password-feedback';

import styles from './styles.module.less';

export function SignUpForm(props) {
  const recaptchaRef = useRef();

  const dispatch = useDispatch();

  const [email, setEmail] = useState(props.email);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [acceptedTC, setAcceptedTC] = useState(false);
  const [captchaSuccess, setCaptchaSuccess] = useState(false);
  const [password, setPassword] = useState('');

  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordViolations, setPasswordViolations] = useState([]);

  useEffect(() => {
    setPasswordViolations(validatePassword(password));
    dispatch(updateLoginPasswordError(null));
  }, [password, confirmPassword, dispatch]);

  useEffect(() => {
    dispatch(updateLoginEmailError(null));
    dispatch(updateLoginPlainError(null));
  }, [
    dispatch,
    email,
    firstName,
    lastName,
    acceptedTC,
    password,
    confirmPassword,
  ]);

  const allFieldsFilled = Boolean(
    email && password && firstName && lastName && confirmPassword
  );

  return (
    <div
      {...(!props.modalMode && {
        className: 'loom-signup-component-email-form',
      })}
    >
      {!props.modalMode && (
        <div className={styles.signupFormBackButton}>
          <TextButton icon={<SvgArrowBack />} onClick={props.onClick}>
            Back
          </TextButton>
        </div>
      )}
      {props.modalMode ? (
        <>
          <Text alignment="left" htmlTag="h1" size="xlarge" fontWeight="bold">
            Create an account
          </Text>
          <Spacer top={2} />
        </>
      ) : (
        <>
          <Text
            htmlTag="h1"
            size="xxlarge"
            fontWeight="bold"
            alignment={'center'}
          >
            What is your name?
          </Text>
          <Spacer y="medium">
            <Text alignment={'center'}>
              This is the name that will appear on your videos.
            </Text>
          </Spacer>
        </>
      )}
      <FormField
        label="Email"
        labelFor="email"
        errorMessage={props.errorMessage}
      >
        <TextInput
          id="email"
          type="email"
          placeholder="Juniper@loom.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onClick={() =>
            analytics.track(SIGNUP_EMAIL_FIELD_CLICKED, {
              source: 'expanded-signup-form',
            })
          }
        />
      </FormField>
      <Spacer top={2} />
      <Arrange
        autoFlow={{
          default: 'row',
          small: 'column',
        }}
        gap="medium"
        justifyContent="stretch"
      >
        <FormField label="First Name" labelFor="first-name">
          <TextInput
            id="first-name"
            placeholder="Juniper"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
          />
        </FormField>
        <FormField label="Last Name" labelFor="last-name">
          <TextInput
            id="last-name"
            placeholder="Fruite"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
          />
        </FormField>
      </Arrange>
      <Spacer top={2} />
      <FormField className="width:full" label="Password" labelFor="password">
        <MaskedInput
          id="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
        />
      </FormField>
      <Spacer top={2} />
      <FormField
        className="width:full"
        label="Confirm Password"
        labelFor="confirm-password"
        errorMessage={props.passwordError || props.plainError}
      >
        <MaskedInput
          id="confirm-password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
        />
      </FormField>
      <Spacer top={1} />
      <PasswordFeedback
        containerProps={{ width: '100%' }}
        passwordViolations={passwordViolations}
      />
      <Spacer top={2} />
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={INVISIBLE_SITE_KEY}
        size="invisible"
        onChange={() => {
          setCaptchaSuccess(true);
        }}
        onExpired={() => {
          recaptchaRef.current.reset();
        }}
        onErrored={() => {
          recaptchaRef.current.reset();
        }}
      />
      <Arrange gap="small">
        <Checkbox
          onChange={() => {
            setAcceptedTC(!acceptedTC);

            if (!acceptedTC) {
              recaptchaRef.current.execute();
            }

            analytics.track(SIGNUP_TOGGLE_TERMS_CHECKBOX, {
              checked: !acceptedTC,
            });
          }}
          isChecked={acceptedTC}
        />
        <Text>
          {'I agree to Loom’s '}
          <Link
            href={TERMS}
            target="_blank"
            onClick={() => {
              analytics.track(SIGNUP_CLICKED_TERMS);
            }}
          >
            Terms
          </Link>
          {' and '}
          <Link
            href={PRIVACY_POLICY}
            target="_blank"
            onClick={() => {
              analytics.track(SIGNUP_CLICKED_PRIVACY);
            }}
          >
            Privacy Policy
          </Link>
        </Text>
      </Arrange>
      <Spacer top={4} />
      <Arrange justifyContent="space-between">
        <div>
          {props.modalMode && (
            <Button icon={<SvgArrowBack />} onClick={props.onClick}>
              Back
            </Button>
          )}
        </div>
        <Button
          icon={!props.loading && <SvgArrowForward />}
          iconPosition="right"
          isDisabled={Boolean(
            !acceptedTC ||
              !captchaSuccess ||
              props.errorStr ||
              !allFieldsFilled ||
              props.loading ||
              passwordViolations.length > 0
          )}
          hasLoader={props.loading}
          variant="primary"
          size={props.buttonSize || (isMobile ? 'large' : 'medium')}
          hasFullWidth={isMobile}
          onClick={() => {
            if (!allFieldsFilled) {
              analytics.track(EMAIL_SIGNUP_FAILED, {
                reason: 'missing_required_fields',
              });

              dispatch(updateLoginPlainError('All fields are required.'));

              return;
            }

            if (!validateEmail(email)) {
              analytics.track(EMAIL_SIGNUP_FAILED, {
                reason: 'email_invalid',
              });

              dispatch(updateLoginEmailError('This email is invalid.'));

              return;
            }

            const passViolations = validatePassword(password);

            if (passViolations.length) {
              analytics.track(EMAIL_SIGNUP_FAILED, {
                reason: 'password_rule_violation',
              });

              dispatch(
                updateLoginPasswordError(
                  explainPasswordViolations(passViolations)
                )
              );

              return;
            }

            const nameError = invalidUserFirstName(firstName);

            if (nameError) {
              analytics.track(EMAIL_SIGNUP_FAILED, {
                reason: 'invalid_first_name',
              });

              dispatch(updateLoginPlainError(nameError));

              return;
            }

            if (password !== confirmPassword) {
              analytics.track(EMAIL_SIGNUP_FAILED, {
                reason: 'passwords_do_not_match',
              });

              dispatch(
                updateLoginPasswordError('Oops! The passwords do not match.')
              );

              return;
            }

            const source = props.source ?? SIGN_UP_PAGE;

            analytics.track(EMAIL_SIGNUP_BUTTON_CLICKED, {
              source,
            });

            incrementMetric('button.click', {
              context: EMAIL_SIGNUP_BUTTON_CLICKED,
              source,
            });

            dispatch(
              signUpUserCreator({
                email,
                password,
                firstName,
                lastName,
                modalMode: props.modalMode,
                callback: props.callback,
                redirectURL: window.location.href, // reload the page
                termsAccepted: acceptedTC,
                inviteLinkId: props.inviteLinkId,
                queryParams: props.onSignupParams,
              })
            );
          }}
          type="submit"
        >
          Continue
        </Button>
      </Arrange>
    </div>
  );
}
